import React, { useState, useEffect } from 'react';
import {
  Box,
  Heading,
  Text,
  VStack,
  HStack,
  Button,
  Flex,
  Icon,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  useToast,
  useDisclosure,
  Progress,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  SimpleGrid
} from '@chakra-ui/react';
import { 
  FaInfoCircle, 
  FaGift, 
  FaWallet, 
  FaTrophy, 
  FaLock,
  FaPlus,
  FaMinus,
  FaChartLine,
  FaCoins,
  FaTicketAlt
} from 'react-icons/fa';
import { CASH_KING_CONFIG } from '../ScratchCardConfigs';
import CashKingCard from '../../CardTypes/CashKingCard';
import { useBalance } from '../../../../../context/BalanceContext';

/**
 * CashKingGame Component
 * 
 * A standalone component for the Cash King game with complete game logic,
 * green-themed UI, and dedicated bounty pool.
 */
const CashKingGame = ({ 
  isPreview = false,
  gameState: propGameState = null
}) => {
  // Game state management
  const [gameState, setGameState] = useState(propGameState);
  const [gameResult, setGameResult] = useState(null);
  
  // User balance from context
  const { balance, tokens, setTokens } = useBalance();
  const [localBalance, setLocalBalance] = useState(balance.jinu || 1500);
  const userBalance = localBalance;
  
  // UI state
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  
  // Cash King specific colors (green theme)
  const colors = {
    primary: '#1b4332', // Main dark green background
    secondary: '#0d3b1f', // Darker green for containers
    accent: 'yellow.400',
    text: '#4fd1c5',
    button: 'green',
    buttonVariant: 'solid',
    progressColor: 'green.400'
  };
  
  // Function to update both local and global balances
  const updateJinuBalance = (amount) => {
    // Update local balance
    setLocalBalance(prev => {
      const newBalance = prev + amount;
      
      // Update global balance in BalanceContext
      balance.jinu = newBalance;
      
      // Update the tokens array
      const updatedTokens = tokens.map(token => 
        token.symbol === 'JINU' ? {...token, balance: newBalance} : token
      );
      setTokens(updatedTokens);
      
      return newBalance;
    });
  };
  
  // Convert prize value string to number (e.g., '5K' to 5000)
  const convertPrizeToNumber = (prizeString) => {
    if (!prizeString) return 0;
    
    let result = 0;
    if (prizeString.includes('K')) {
      result = parseInt(prizeString.replace('K', '')) * 1000;
    } else {
      result = parseInt(prizeString);
    }
    
    return result;
  };
  
  // Initialize game state
  const initializeGame = (isWinning = false) => {
    const totalCells = CASH_KING_CONFIG.gridSize.rows * CASH_KING_CONFIG.gridSize.columns;
    const cellValues = [];
    const winningCells = [];
    
    // Determine if this card is a winner based on the configured probability
    const isWinningCard = isWinning || (Math.random() < CASH_KING_CONFIG.winProbability);
    
    // Select prize values based on the configured prize distribution
    const selectedPrizes = [];
    const winCount = CASH_KING_CONFIG.winningSymbols || 3;
    
    // This will track which prize value is used for the winning match
    let winningPrizeValue = null;
    
    if (isWinningCard) {
      // For winning cards, select a prize based on the actual prize distribution
      const mainPrize = CASH_KING_CONFIG.prizes[Math.floor(Math.random() * CASH_KING_CONFIG.prizes.length)];
      selectedPrizes.push(mainPrize);
      
      // Add additional winning symbols with lower values
      for (let i = 1; i < winCount; i++) {
        // For additional winning symbols, use lower prize values
        const lowerPrizes = CASH_KING_CONFIG.prizes.filter(prize => {
          const prizeValue = convertPrizeToNumber(prize);
          const mainPrizeValue = convertPrizeToNumber(mainPrize);
          return prizeValue < mainPrizeValue;
        });
        
        if (lowerPrizes.length > 0) {
          const randomIndex = Math.floor(Math.random() * lowerPrizes.length);
          selectedPrizes.push(lowerPrizes[randomIndex]);
        } else {
          // Fallback if no lower prizes available
          selectedPrizes.push(mainPrize);
        }
      }
      
      // Shuffle the prizes to randomize their positions
      selectedPrizes.sort(() => Math.random() - 0.5);
      
      // Randomly select winning cell indices
      while (winningCells.length < winCount) {
        const randomIndex = Math.floor(Math.random() * totalCells);
        if (!winningCells.includes(randomIndex)) {
          winningCells.push(randomIndex);
        }
      }
    } else {
      // For non-winning cards, just use random low-value prizes that don't match
      for (let i = 0; i < winCount; i++) {
        const lowerPrizes = CASH_KING_CONFIG.prizes.slice(0, 4); // Use the lowest prize values
        const randomIndex = Math.floor(Math.random() * lowerPrizes.length);
        selectedPrizes.push(lowerPrizes[randomIndex]);
      }
      
      // Make sure prizes don't match by adjusting if needed
      if (selectedPrizes.every(prize => prize === selectedPrizes[0])) {
        if (selectedPrizes.length > 1) {
          selectedPrizes[1] = CASH_KING_CONFIG.prizes[0];
        }
        if (selectedPrizes.length > 2) {
          selectedPrizes[2] = CASH_KING_CONFIG.prizes[1];
        }
      }
    }
    
    // For winning cards, we need to ensure there are 3 matching values
    if (isWinningCard) {
      // Choose one of the selected prizes to be the winning match
      winningPrizeValue = selectedPrizes[Math.floor(Math.random() * selectedPrizes.length)];
      
      // Select 3 random positions for the winning value
      const winPositions = [];
      while (winPositions.length < 3) {
        const randomPos = Math.floor(Math.random() * totalCells);
        if (!winPositions.includes(randomPos)) {
          winPositions.push(randomPos);
        }
      }
      
      // Fill the grid with values
      for (let i = 0; i < totalCells; i++) {
        if (winPositions.includes(i)) {
          // Put the winning value in the selected positions
          cellValues.push(winningPrizeValue);
        } else {
          // For non-winning positions, use values that won't create additional matches
          const availablePrizes = CASH_KING_CONFIG.prizes.filter(prize => prize !== winningPrizeValue);
          const randomIndex = Math.floor(Math.random() * availablePrizes.length);
          const randomPrize = availablePrizes[randomIndex];
          
          // Check if adding this prize would create an unintended match
          const existingCount = cellValues.filter(value => value === randomPrize).length;
          if (existingCount >= 2) {
            // If this would create an unintended match, use a different prize
            const alternativePrize = availablePrizes[(randomIndex + 1) % availablePrizes.length];
            cellValues.push(alternativePrize);
          } else {
            cellValues.push(randomPrize);
          }
        }
      }
    } else {
      // For non-winning cards, ensure no 3 matching values
      // First fill the grid with random values
      for (let i = 0; i < totalCells; i++) {
        const randomPrizeIndex = Math.floor(Math.random() * CASH_KING_CONFIG.prizes.length);
        const randomPrize = CASH_KING_CONFIG.prizes[randomPrizeIndex];
        
        // Check if adding this prize would create a match
        const existingCount = cellValues.filter(value => value === randomPrize).length;
        if (existingCount >= 2) {
          // If this would create a match, use a different prize
          const alternativePrize = CASH_KING_CONFIG.prizes[(randomPrizeIndex + 1) % CASH_KING_CONFIG.prizes.length];
          cellValues.push(alternativePrize);
        } else {
          cellValues.push(randomPrize);
        }
      }
      
      // Verify no unintended matches
      for (const prize of selectedPrizes) {
        const count = cellValues.filter(value => value === prize).length;
        if (count >= 3) {
          // If we accidentally created a match, replace one occurrence
          const indexToReplace = cellValues.findIndex(value => value === prize);
          if (indexToReplace !== -1) {
            // Replace with a different prize
            const alternativePrizes = CASH_KING_CONFIG.prizes.filter(p => p !== prize);
            cellValues[indexToReplace] = alternativePrizes[Math.floor(Math.random() * alternativePrizes.length)];
          }
        }
      }
    }
    
    // Calculate win amount based on the matching prize value
    let winAmount = 0;
    if (isWinningCard && winningPrizeValue) {
      // The win amount is the value of the matching prize
      winAmount = convertPrizeToNumber(winningPrizeValue);
    }
    
    // Create the game state
    return {
      status: 'active',
      cellValues,
      winningCells,
      revealedCells: [],
      isWinningCard,
      winAmount,
      winningSymbolsRevealed: false, // Initially, winning symbols are not revealed
      prizeValues: selectedPrizes, // Store the selected prize values for this card
      winningPrizeValue // Store the prize value that creates the winning match
    };
  };
  
  // Handle buying a card
  const handleBuy = () => {
    // Check if user has enough balance
    if (userBalance < CASH_KING_CONFIG.cost) {
      toast({
        title: "Insufficient balance",
        description: `You need ${CASH_KING_CONFIG.cost} JINU to play this game.`,
        status: "error",
        duration: 5000,
        isClosable: true
      });
      return;
    }
    
    // Deduct the cost from user balance
    updateJinuBalance(-CASH_KING_CONFIG.cost);
    
    // Initialize game state
    let isWinningCard = Math.random() <= CASH_KING_CONFIG.winProbability;
    
    // Create a fresh game state
    const newGameState = initializeGame(isWinningCard);
    
    // Reset any existing state
    setGameState(null);
    
    // Use setTimeout to ensure the component has time to reset before setting the new state
    setTimeout(() => {
      setGameState(newGameState);
    }, 50);
    
    // Close any open modals
    if (isOpen) {
      onClose();
    }
    
    // Reset game result
    setGameResult(null);
    
    toast({
      title: "Card purchased",
      description: "Click 'Reveal Winning Symbols' to start!",
      status: "success",
      duration: 3000,
      isClosable: true
    });
  };
  
  // Check for winning combinations in the revealed cells
  const checkForWinningCombination = () => {
    if (!gameState || !gameState.cellValues) return null;
    
    // Count occurrences of each value in the revealed cells
    const valueCounts = {};
    gameState.revealedCells.forEach(index => {
      const value = gameState.cellValues[index];
      if (value) { // Only count non-empty values
        valueCounts[value] = (valueCounts[value] || 0) + 1;
      }
    });
    
    // Check if any value appears 3 or more times
    for (const [value, count] of Object.entries(valueCounts)) {
      if (count >= 3 && gameState.prizeValues && gameState.prizeValues.includes(value)) {
        // Found a winning combination
        return value;
      }
    }
    
    return null;
  };
  
  // Handle revealing winning symbols first, then individual cells
  const handleReveal = (index) => {
    if (!gameState || gameState.status !== 'active') return;
    
    // Create a copy of the current game state
    const newState = { ...gameState };
    
    // If winning symbols haven't been revealed yet, reveal them first
    if (!newState.winningSymbolsRevealed) {
      newState.winningSymbolsRevealed = true;
      
      // Determine the potential win amount based on the prize values
      if (newState.isWinningCard && newState.prizeValues && newState.prizeValues.length > 0) {
        // Select the highest prize value for this winning card
        const highestPrize = newState.prizeValues.reduce((max, prize) => {
          const prizeValue = convertPrizeToNumber(prize);
          return prizeValue > max ? prizeValue : max;
        }, 0);
        
        // Set the potential win amount
        newState.potentialWinAmount = highestPrize;
      }
      
      setGameState(newState);
      
      // Show toast for winning symbols revealed
      toast({
        title: "Winning Symbols Revealed",
        description: "Now scratch the card to reveal your symbols!",
        status: "info",
        duration: 2000,
        isClosable: true
      });
      
      return; // Exit early, wait for next click to reveal cells
    }
    
    // Add this cell to the revealed cells if it's not already there
    if (!newState.revealedCells.includes(index)) {
      newState.revealedCells.push(index);
    }
    
    // Check if we have a winning combination
    const matchedValue = checkForWinningCombination();
    
    if (matchedValue) {
      newState.status = 'won';
      
      // Get the win amount from the matched value
      const winAmount = convertPrizeToNumber(matchedValue);
      newState.winAmount = winAmount;
      
      // Update user balance
      updateJinuBalance(winAmount);
      
      // Set game result for modal
      setGameResult({
        matchedValue,
        amount: winAmount
      });
      
      // Show win modal
      onOpen();
    }
    
    // Check if all cells have been revealed
    const totalCells = CASH_KING_CONFIG.gridSize.rows * CASH_KING_CONFIG.gridSize.columns;
    if (newState.revealedCells.length === totalCells) {
      // All cells revealed, game is complete
      if (newState.status === 'active') {
        // If the game is still active (not won), mark it as lost
        newState.status = 'lost';
        
        toast({
          title: "Better luck next time!",
          description: "Try again for a chance to win big!",
          status: "info",
          duration: 3000,
          isClosable: true
        });
      }
    }
    
    // Update the game state
    setGameState(newState);
  };
  
  // Handle revealing all cells at once
  const handleRevealAll = () => {
    if (!gameState || gameState.status !== 'active') return;
    
    // Create a copy of the current game state
    const newState = { ...gameState };
    
    // If winning symbols haven't been revealed yet, reveal them first
    if (!newState.winningSymbolsRevealed) {
      newState.winningSymbolsRevealed = true;
      setGameState(newState);
      
      // Show toast for winning symbols revealed
      toast({
        title: "Winning Symbols Revealed",
        description: "Now scratch the card to reveal your symbols!",
        status: "info",
        duration: 2000,
        isClosable: true
      });
      
      return; // Exit early, wait for next click to reveal all cells
    }
    
    // Reveal all cells
    const totalCells = CASH_KING_CONFIG.gridSize.rows * CASH_KING_CONFIG.gridSize.columns;
    newState.revealedCells = Array.from({ length: totalCells }, (_, i) => i);
    
    // Check if we have a winning combination
    const matchedValue = checkForWinningCombination();
    
    if (matchedValue) {
      newState.status = 'won';
      
      // Get the win amount from the matched value
      const winAmount = convertPrizeToNumber(matchedValue);
      newState.winAmount = winAmount;
      
      // Update user balance
      updateJinuBalance(winAmount);
      
      // Set game result for modal
      setGameResult({
        matchedValue,
        amount: winAmount
      });
      
      // Show win modal
      onOpen();
    } else {
      // No winning combination, game is lost
      newState.status = 'lost';
      
      toast({
        title: "Better luck next time!",
        description: "Try again for a chance to win big!",
        status: "info",
        duration: 3000,
        isClosable: true
      });
    }
    
    // Update the game state
    setGameState(newState);
  };
  
  // Handle playing again
  const handlePlayAgain = () => {
    // Close the win modal
    onClose();
    
    // Buy a new card
    handleBuy();
  };
  
  // Handle sending as gift
  const handleGift = () => {
    // Check if user has enough balance
    if (userBalance < CASH_KING_CONFIG.cost) {
      toast({
        title: "Insufficient balance",
        description: `You need ${CASH_KING_CONFIG.cost} JINU to gift this card.`,
        status: "error",
        duration: 5000,
        isClosable: true
      });
      return;
    }
    
    toast({
      title: "Gift feature",
      description: "Send this card as a gift to a friend!",
      status: "info",
      duration: 3000,
      isClosable: true
    });
  };
  
  // Get bounty pool data
  const getBountyPoolData = () => {
    return {
      totalCards: 10000,
      cardsMinted: 2500,
      cardsRemaining: 7500,
      totalPrizePool: 2500000,
      claimedPrizes: 625000,
      remainingPrizes: 1875000,
      topPrizes: {
        total: 15,
        claimed: 3,
        remaining: 12
      },
      recentWinners: [
        { player: '0x8b4...7f2e', amount: 100000, timestamp: Date.now() - 1000 * 60 * 5, type: '100K' },
        { player: '0x3c9...4d7c', amount: 5000, timestamp: Date.now() - 1000 * 60 * 15, type: '5K' },
        { player: '0x2f2...9e8d', amount: 0, timestamp: Date.now() - 1000 * 60 * 22, type: 'GIFT BOX' },
        { player: '0x7c4...1b1d', amount: 1000, timestamp: Date.now() - 1000 * 60 * 38, type: '1K' },
        { player: '0x5a7...3d3f', amount: 25000, timestamp: Date.now() - 1000 * 60 * 55, type: '25K' },
      ],
      prizeSummary: {
        topPrizes: { total: 10, claimed: 2 },
        midPrizes: { total: 350, claimed: 87 },
        lowPrizes: { total: 18500, claimed: 4632 },
        specialRewards: { total: 42614, claimed: 8639 }
      }
    };
  };
  
  // Initialize game on component mount
  useEffect(() => {
    if (!gameState && !isPreview) {
      // Auto-initialize game state for better UX
      const initialState = initializeGame(false);
      setGameState(initialState);
    }
  }, []);

  return (
    <Box width="100%" minHeight="auto" p={4}>
      <Flex direction={{ base: 'column', md: 'row' }} width="100%" gap={3}>
        {/* Game Area - Left side, card */}
        <Box width={{ base: "100%", md: "auto" }}>
          <CashKingCard 
            isPreview={isPreview}
            gameState={gameState}
            onReveal={handleReveal}
          />
        </Box>
        
        {/* Game Controls - Middle, green themed */}
        <Box width={{ base: "100%", md: "400px" }} ml={3}>
          <Box 
            bg="rgba(0, 30, 15, 0.95)" 
            p={5} 
            borderRadius="lg"
            mb={4}
            width="100%"
            boxShadow="0 8px 32px 0 rgba(0, 0, 0, 0.5)"
            border="1px solid rgba(0, 255, 128, 0.15)"
            backdropFilter="blur(10px)"
          >
            <VStack spacing={4} align="stretch">
              <Heading size="md" color="#4ADE80">Game Controls</Heading>
              
              {/* Buy and Send Gift buttons side by side */}
              <HStack spacing={4}>
                <Button 
                  colorScheme="yellow" 
                  size="lg"
                  width="100%"
                  onClick={handleBuy}
                  isDisabled={gameState && gameState.status === 'active'}
                >
                  Buy ({CASH_KING_CONFIG.cost} JINU)
                </Button>
                
                <Button 
                  colorScheme="purple" 
                  size="lg"
                  width="100%"
                  leftIcon={<FaGift />}
                  onClick={handleGift}
                >
                  Send As Gift
                </Button>
              </HStack>
              
              {/* Reveal button below */}
              {gameState && gameState.status === 'active' && (
                <Button
                  colorScheme="yellow"
                  size="md"
                  width="100%"
                  onClick={handleRevealAll}
                  isDisabled={!gameState || gameState.status !== 'active'}
                  mb={2}
                >
                  {gameState && !gameState.winningSymbolsRevealed ? 'Reveal Winning Symbols' : 'Reveal All'}
                </Button>
              )}
              
              <Box bg="blackAlpha.400" p={3} borderRadius="md">
                <HStack mb={2}>
                  <Icon as={FaInfoCircle} color={colors.accent} />
                  <Heading size="sm" color="white">How to Play:</Heading>
                </HStack>
                <Text color="white" fontSize="sm">
                  Click on each square to reveal symbols. Match 3 of the
                  winning symbols shown above to win their prize value.
                </Text>
              </Box>
            </VStack>
          </Box>
        </Box>
        
        {/* Create Game Box - Right side, green themed */}
        <Box width={{ base: "100%", md: "400px" }}>
          <Box 
            bg="rgba(0, 30, 15, 0.95)" 
            p={5} 
            borderRadius="lg"
            mb={4}
            width="100%"
            boxShadow="0 8px 32px 0 rgba(0, 0, 0, 0.5)"
            border="1px solid rgba(0, 255, 128, 0.15)"
            backdropFilter="blur(10px)"
          >
            <VStack spacing={4} align="stretch">
              <Heading size="md" color="#4ADE80" mb={2}>Create Your Game</Heading>
              
              {/* Card Name Input */}
              <Box>
                <Text color="gray.300" mb={1}>Card Name</Text>
                <Box 
                  bg="whiteAlpha.100" 
                  p={2} 
                  borderRadius="md" 
                  border="1px solid" 
                  borderColor="whiteAlpha.300"
                >
                  <Text color="gray.400">e.g. ROYAL FLUSH</Text>
                </Box>
              </Box>
              
              {/* Cost Input */}
              <Box>
                <Text color="gray.300" mb={1}>Cost (JINU)</Text>
                <HStack 
                  bg="whiteAlpha.100" 
                  p={2} 
                  borderRadius="md" 
                  border="1px solid" 
                  borderColor="whiteAlpha.300"
                  justify="space-between"
                >
                  <Text color="white">200</Text>
                  <HStack spacing={1}>
                    <Box 
                      as="button" 
                      bg="whiteAlpha.200" 
                      borderRadius="md" 
                      px={2} 
                      _hover={{ bg: "whiteAlpha.300" }}
                    >
                      <Text fontSize="lg">▲</Text>
                    </Box>
                    <Box 
                      as="button" 
                      bg="whiteAlpha.200" 
                      borderRadius="md" 
                      px={2} 
                      _hover={{ bg: "whiteAlpha.300" }}
                    >
                      <Text fontSize="lg">▼</Text>
                    </Box>
                  </HStack>
                </HStack>
              </Box>
              
              {/* Number of Cards Input */}
              <Box>
                <Text color="gray.300" mb={1}>Number of Cards</Text>
                <HStack 
                  bg="whiteAlpha.100" 
                  p={2} 
                  borderRadius="md" 
                  border="1px solid" 
                  borderColor="whiteAlpha.300"
                  justify="space-between"
                >
                  <Text color="white">1000</Text>
                  <HStack spacing={1}>
                    <Box 
                      as="button" 
                      bg="whiteAlpha.200" 
                      borderRadius="md" 
                      px={2} 
                      _hover={{ bg: "whiteAlpha.300" }}
                    >
                      <Text fontSize="lg">▲</Text>
                    </Box>
                    <Box 
                      as="button" 
                      bg="whiteAlpha.200" 
                      borderRadius="md" 
                      px={2} 
                      _hover={{ bg: "whiteAlpha.300" }}
                    >
                      <Text fontSize="lg">▼</Text>
                    </Box>
                  </HStack>
                </HStack>
                <Text color="gray.500" fontSize="xs" mt={1}>Maximum 1000 cards</Text>
              </Box>
              
              {/* Connect Wallet Button */}
              <Button
                leftIcon={<FaWallet />}
                colorScheme="green"
                bg="rgba(0, 40, 20, 0.8)"
                border="1px solid rgba(0, 255, 128, 0.2)"
                width="100%"
                mt={2}
              >
                Connect Wallet & Add Prizes
              </Button>
              
              {/* Prize Pool Info */}
              <Box bg="whiteAlpha.100" p={3} borderRadius="md">
                <Text color="white" fontWeight="bold" mb={2}>Build Your Prize Pool</Text>
                <Text color="gray.300" fontSize="sm">
                  Add tokens and NFTs from your wallet to create prizes:
                </Text>
                <VStack align="start" mt={2} spacing={1} color="gray.300" fontSize="sm">
                  <Text>• 25k JINU - 1 prize</Text>
                  <Text>• 5k JINU - 5 prizes</Text>
                  <Text>• NFTs - 10 prizes</Text>
                </VStack>
              </Box>
              
              {/* Create Game Button */}
              <Button
                colorScheme="green"
                bg="rgba(0, 40, 20, 0.8)"
                border="1px solid rgba(0, 255, 128, 0.2)"
                width="100%"
                size="lg"
                mt={6}>
                Create Game
              </Button>
            </VStack>
          </Box>
        </Box>
      </Flex>
      
      {/* Bounty Pool Section - Green themed with glassmorphism */}
      <Box width="100%" mt={6}>
        <Box 
          bg="rgba(0, 30, 15, 0.95)" 
          p={5} 
          borderRadius="lg"
          boxShadow="0 8px 32px 0 rgba(0, 0, 0, 0.5)"
          border="1px solid rgba(0, 255, 128, 0.15)"
          backdropFilter="blur(10px)"
          position="relative"
          overflow="hidden"
          _before={{
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(135deg, rgba(0, 100, 50, 0.1) 0%, rgba(0, 40, 20, 0.1) 100%)',
            zIndex: 0,
          }}
        >
          <Heading size="lg" color="#4ADE80" mb={6} display="flex" alignItems="center" position="relative" zIndex={1}>
            <Icon as={FaTrophy} mr={2} color="yellow.400" /> Cash King Bounty Pool
          </Heading>
          
          {/* Stats Boxes */}
          <SimpleGrid columns={{ base: 1, sm: 2, md: 5 }} spacing={4} mb={6} position="relative" zIndex={1}>
            {/* Overall RTP */}
            <Box 
              bg="rgba(0, 40, 20, 0.8)" 
              p={4} 
              borderRadius="lg" 
              boxShadow="0 4px 12px rgba(0, 0, 0, 0.3)"
              textAlign="center"
              border="1px solid rgba(0, 255, 128, 0.2)"
              position="relative"
              overflow="hidden"
            >
              <VStack spacing={1}>
                <Icon 
                  as={FaChartLine} 
                  color="#A855F7" 
                  boxSize={6} 
                  mb={1}
                />
                <Text color="#4ADE80" fontSize="sm" fontWeight="bold">Overall RTP</Text>
                <Text color="white" fontSize="xl" fontWeight="bold">90%</Text>
                <Text color="#D946EF" fontSize="xs">75% MONETARY</Text>
                <Text color="#38BDF8" fontSize="xs">+15% BONUSES</Text>
              </VStack>
            </Box>
            
            {/* Prize Pool */}
            <Box 
              bg="rgba(0, 40, 20, 0.8)" 
              p={4} 
              borderRadius="lg" 
              boxShadow="0 4px 12px rgba(0, 0, 0, 0.3)"
              textAlign="center"
              border="1px solid rgba(0, 255, 128, 0.2)"
              position="relative"
              overflow="hidden"
            >
              <VStack spacing={1}>
                <Icon 
                  as={FaCoins} 
                  color="#FACC15" 
                  boxSize={6} 
                  mb={1}
                />
                <Text color="#4ADE80" fontSize="sm" fontWeight="bold">Prize Pool</Text>
                <Text color="white" fontSize="xl" fontWeight="bold" whiteSpace="nowrap">2,500,000 JINU</Text>
                <Text color="#FACC15" fontSize="xs">~$2,500 USD</Text>
              </VStack>
            </Box>
            
            {/* Cards Minted */}
            <Box 
              bg="rgba(0, 40, 20, 0.8)" 
              p={4} 
              borderRadius="lg" 
              boxShadow="0 4px 12px rgba(0, 0, 0, 0.3)"
              textAlign="center"
              border="1px solid rgba(0, 255, 128, 0.2)"
              position="relative"
              overflow="hidden"
            >
              <VStack spacing={1}>
                <Icon 
                  as={FaTicketAlt} 
                  color="#38BDF8" 
                  boxSize={6} 
                  mb={1}
                />
                <Text color="#4ADE80" fontSize="sm" fontWeight="bold">Cards Minted</Text>
                <Text color="white" fontSize="xl" fontWeight="bold">2,500</Text>
                <Text color="#38BDF8" fontSize="xs">7,500 remaining</Text>
              </VStack>
            </Box>
            
            {/* Prizes Claimed */}
            <Box 
              bg="rgba(0, 40, 20, 0.8)" 
              p={4} 
              borderRadius="lg" 
              boxShadow="0 4px 12px rgba(0, 0, 0, 0.3)"
              textAlign="center"
              border="1px solid rgba(0, 255, 128, 0.2)"
              position="relative"
              overflow="hidden"
            >
              <VStack spacing={1}>
                <Icon 
                  as={FaGift} 
                  color="#4ADE80" 
                  boxSize={6} 
                  mb={1}
                />
                <Text color="#4ADE80" fontSize="sm" fontWeight="bold">Prizes Claimed</Text>
                <Text color="white" fontSize="xl" fontWeight="bold" whiteSpace="nowrap">375,000 JINU</Text>
                <Text color="#4ADE80" fontSize="xs">4,632 winners</Text>
              </VStack>
            </Box>
            
            {/* Top Prizes Left */}
            <Box 
              bg="rgba(0, 40, 20, 0.8)" 
              p={4} 
              borderRadius="lg" 
              boxShadow="0 4px 12px rgba(0, 0, 0, 0.3)"
              textAlign="center"
              border="1px solid rgba(0, 255, 128, 0.2)"
              position="relative"
              overflow="hidden"
            >
              <VStack spacing={1}>
                <Icon 
                  as={FaTrophy} 
                  color="#FACC15" 
                  boxSize={6} 
                  mb={1}
                />
                <Text color="#4ADE80" fontSize="sm" fontWeight="bold">Top Prizes Left</Text>
                <Text color="white" fontSize="xl" fontWeight="bold">5</Text>
                <Text color="#FACC15" fontSize="xs">1X 100K JINU</Text>
                <Text color="#FACC15" fontSize="xs">4X 50K JINU</Text>
              </VStack>
            </Box>
          </SimpleGrid>
          
          {/* Progress Bars */}
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6} mb={6} position="relative" zIndex={1}>
            {/* Minting Progress */}
            <Box 
              bg="rgba(0, 30, 15, 0.8)"
              p={4}
              borderRadius="lg"
              border="1px solid rgba(0, 255, 128, 0.1)"
            >
              <Flex justify="space-between" align="center">
                <Text color="#4ADE80" fontSize="sm" fontWeight="bold">Minting Progress</Text>
                <Flex direction="column" align="flex-end">
                  <Text color="white" fontSize="sm">2,500 minted</Text>
                  <Text color="#4ADE80" fontSize="xs">10,000 total</Text>
                </Flex>
              </Flex>
              <Box 
                position="relative" 
                height="8px" 
                width="100%" 
                bg="rgba(0, 20, 10, 0.5)" 
                borderRadius="full"
                overflow="hidden"
                mt={2}
              >
                <Box 
                  position="absolute" 
                  top={0} 
                  left={0} 
                  height="100%" 
                  width="25%" 
                  bg="linear-gradient(90deg, #A855F7 0%, #8B5CF6 100%)"
                  borderRadius="full"
                />
              </Box>
            </Box>
            
            {/* Prize Claims Progress */}
            <Box 
              bg="rgba(0, 30, 15, 0.8)"
              p={4}
              borderRadius="lg"
              border="1px solid rgba(0, 255, 128, 0.1)"
            >
              <Flex justify="space-between" align="center">
                <Text color="#4ADE80" fontSize="sm" fontWeight="bold">Prize Claims Progress</Text>
                <Flex direction="column" align="flex-end">
                  <Text color="white" fontSize="sm">375,000 JINU claimed</Text>
                  <Text color="#4ADE80" fontSize="xs">1,500,000 JINU total</Text>
                </Flex>
              </Flex>
              <Box 
                position="relative" 
                height="8px" 
                width="100%" 
                bg="rgba(0, 20, 10, 0.5)" 
                borderRadius="full"
                overflow="hidden"
                mt={2}
              >
                <Box 
                  position="absolute" 
                  top={0} 
                  left={0} 
                  height="100%" 
                  width="25%" 
                  bg="linear-gradient(90deg, #4ADE80 0%, #22C55E 100%)"
                  borderRadius="full"
                />
              </Box>
            </Box>
          </SimpleGrid>
          
          {/* Prize Distribution and Recent Claims Section */}
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6} mb={6} position="relative" zIndex={1}>
            {/* Prize Distribution Table */}
            <Box 
              bg="rgba(0, 30, 15, 0.8)" 
              borderRadius="lg" 
              p={4}
              border="1px solid rgba(0, 255, 128, 0.1)"
              position="relative"
              zIndex={1}
              overflow="hidden"
            >
              <Heading size="sm" color="#4ADE80" mb={4} position="relative" zIndex={1}>Prize Distribution</Heading>
              <Box overflowX="auto">
                <Table size="sm" variant="unstyled" position="relative" zIndex={1}>
                  <Thead>
                    <Tr>
                      <Th color="#4ADE80">PRIZE</Th>
                      <Th color="#4ADE80">TOTAL</Th>
                      <Th color="#4ADE80">CLAIMED</Th>
                      <Th color="#4ADE80">REMAIN</Th>
                      <Th color="#4ADE80">ODDS</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    <Tr>
                      <Td>
                        <Box 
                          bg="linear-gradient(90deg, rgba(168, 85, 247, 0.8) 0%, rgba(139, 92, 246, 0.8) 100%)" 
                          borderRadius="md" 
                          py={1} 
                          px={3} 
                          display="inline-block"
                          boxShadow="0 0 4px rgba(255, 255, 255, 0.15), 0 0 15px rgba(255, 255, 255, 0.1)"
                          border="0.75px solid rgba(255, 255, 255, 0.5)"
                          backdropFilter="blur(4px)"
                          minWidth="140px"
                          textAlign="center"
                          whiteSpace="nowrap"
                        >
                          <Text color="white" fontWeight="extrabold" fontSize="sm" letterSpacing="tight" textShadow="0 2px 5px rgba(0, 0, 0, 0.7)">100,000 JINU</Text>
                        </Box>
                      </Td>
                      <Td color="white">1</Td>
                      <Td color="green.400">0</Td>
                      <Td color="#4ADE80">1</Td>
                      <Td color="yellow.300">1:10000</Td>
                    </Tr>
                    <Tr>
                      <Td>
                        <Box 
                          bg="linear-gradient(90deg, rgba(217, 70, 239, 0.8) 0%, rgba(192, 38, 211, 0.8) 100%)" 
                          borderRadius="md" 
                          py={1} 
                          px={3} 
                          display="inline-block"
                          boxShadow="0 0 4px rgba(255, 255, 255, 0.15), 0 0 15px rgba(255, 255, 255, 0.1)"
                          border="0.75px solid rgba(255, 255, 255, 0.5)"
                          backdropFilter="blur(4px)"
                          minWidth="140px"
                          textAlign="center"
                          whiteSpace="nowrap"
                        >
                          <Text color="white" fontWeight="extrabold" fontSize="sm" letterSpacing="tight" textShadow="0 2px 5px rgba(0, 0, 0, 0.7)">50,000 JINU</Text>
                        </Box>
                      </Td>
                      <Td color="white">2</Td>
                      <Td color="green.400">0</Td>
                      <Td color="#4ADE80">2</Td>
                      <Td color="yellow.300">1:5000</Td>
                    </Tr>
                    <Tr>
                      <Td>
                        <Box 
                          bg="linear-gradient(90deg, rgba(139, 92, 246, 0.8) 0%, rgba(124, 58, 237, 0.8) 100%)" 
                          borderRadius="md" 
                          py={1} 
                          px={3} 
                          display="inline-block"
                          boxShadow="0 0 4px rgba(255, 255, 255, 0.15), 0 0 15px rgba(255, 255, 255, 0.1)"
                          border="0.75px solid rgba(255, 255, 255, 0.5)"
                          backdropFilter="blur(4px)"
                          minWidth="140px"
                          textAlign="center"
                          whiteSpace="nowrap"
                        >
                          <Text color="white" fontWeight="extrabold" fontSize="sm" letterSpacing="tight" textShadow="0 2px 5px rgba(0, 0, 0, 0.7)">25,000 JINU</Text>
                        </Box>
                      </Td>
                      <Td color="white">4</Td>
                      <Td color="green.400">1</Td>
                      <Td color="#4ADE80">3</Td>
                      <Td color="yellow.300">1:2500</Td>
                    </Tr>
                    <Tr>
                      <Td>
                        <Box 
                          bg="linear-gradient(90deg, rgba(59, 130, 246, 0.8) 0%, rgba(37, 99, 235, 0.8) 100%)" 
                          borderRadius="md" 
                          py={1} 
                          px={3} 
                          display="inline-block"
                          boxShadow="0 0 4px rgba(255, 255, 255, 0.15), 0 0 15px rgba(255, 255, 255, 0.1)"
                          border="0.75px solid rgba(255, 255, 255, 0.5)"
                          backdropFilter="blur(4px)"
                          minWidth="140px"
                          textAlign="center"
                          whiteSpace="nowrap"
                        >
                          <Text color="white" fontWeight="extrabold" fontSize="sm" letterSpacing="tight" textShadow="0 2px 5px rgba(0, 0, 0, 0.7)">10,000 JINU</Text>
                        </Box>
                      </Td>
                      <Td color="white">5</Td>
                      <Td color="green.400">1</Td>
                      <Td color="#4ADE80">4</Td>
                      <Td color="yellow.300">1:2000</Td>
                    </Tr>
                    <Tr>
                      <Td>
                        <Box 
                          bg="linear-gradient(90deg, rgba(14, 165, 233, 0.8) 0%, rgba(2, 132, 199, 0.8) 100%)" 
                          borderRadius="md" 
                          py={1} 
                          px={3} 
                          display="inline-block"
                          boxShadow="0 0 4px rgba(255, 255, 255, 0.15), 0 0 15px rgba(255, 255, 255, 0.1)"
                          border="0.75px solid rgba(255, 255, 255, 0.5)"
                          backdropFilter="blur(4px)"
                          minWidth="140px"
                          textAlign="center"
                          whiteSpace="nowrap"
                        >
                          <Text color="white" fontWeight="extrabold" fontSize="sm" letterSpacing="tight" textShadow="0 2px 5px rgba(0, 0, 0, 0.7)">5,000 JINU</Text>
                        </Box>
                      </Td>
                      <Td color="white">10</Td>
                      <Td color="green.400">3</Td>
                      <Td color="#4ADE80">7</Td>
                      <Td color="yellow.300">1:1000</Td>
                    </Tr>
                    <Tr>
                      <Td>
                        <Box 
                          bg="linear-gradient(90deg, rgba(6, 182, 212, 0.8) 0%, rgba(8, 145, 178, 0.8) 100%)" 
                          borderRadius="md" 
                          py={1} 
                          px={3} 
                          display="inline-block"
                          boxShadow="0 0 4px rgba(255, 255, 255, 0.15), 0 0 15px rgba(255, 255, 255, 0.1)"
                          border="0.75px solid rgba(255, 255, 255, 0.5)"
                          backdropFilter="blur(4px)"
                          minWidth="140px"
                          textAlign="center"
                          whiteSpace="nowrap"
                        >
                          <Text color="white" fontWeight="extrabold" fontSize="sm" letterSpacing="tight" textShadow="0 2px 5px rgba(0, 0, 0, 0.7)">2,000 JINU</Text>
                        </Box>
                      </Td>
                      <Td color="white">20</Td>
                      <Td color="green.400">6</Td>
                      <Td color="#4ADE80">14</Td>
                      <Td color="yellow.300">1:500</Td>
                    </Tr>
                    <Tr>
                      <Td>
                        <Box 
                          bg="linear-gradient(90deg, rgba(16, 185, 129, 0.8) 0%, rgba(5, 150, 105, 0.8) 100%)" 
                          borderRadius="md" 
                          py={1} 
                          px={3} 
                          display="inline-block"
                          boxShadow="0 0 4px rgba(255, 255, 255, 0.15), 0 0 15px rgba(255, 255, 255, 0.1)"
                          border="0.75px solid rgba(255, 255, 255, 0.5)"
                          backdropFilter="blur(4px)"
                          minWidth="140px"
                          textAlign="center"
                          whiteSpace="nowrap"
                        >
                          <Text color="white" fontWeight="extrabold" fontSize="sm" letterSpacing="tight" textShadow="0 2px 5px rgba(0, 0, 0, 0.7)">1,000 JINU</Text>
                        </Box>
                      </Td>
                      <Td color="white">40</Td>
                      <Td color="green.400">12</Td>
                      <Td color="#4ADE80">28</Td>
                      <Td color="yellow.300">1:250</Td>
                    </Tr>
                    <Tr>
                      <Td>
                        <Box 
                          bg="linear-gradient(90deg, rgba(34, 197, 94, 0.8) 0%, rgba(22, 163, 74, 0.8) 100%)" 
                          borderRadius="md" 
                          py={1} 
                          px={3} 
                          display="inline-block"
                          boxShadow="0 0 4px rgba(255, 255, 255, 0.15), 0 0 15px rgba(255, 255, 255, 0.1)"
                          border="0.75px solid rgba(255, 255, 255, 0.5)"
                          backdropFilter="blur(4px)"
                          minWidth="140px"
                          textAlign="center"
                          whiteSpace="nowrap"
                        >
                          <Text color="white" fontWeight="extrabold" fontSize="sm" letterSpacing="tight" textShadow="0 2px 5px rgba(0, 0, 0, 0.7)">500 JINU</Text>
                        </Box>
                      </Td>
                      <Td color="white">100</Td>
                      <Td color="green.400">30</Td>
                      <Td color="#4ADE80">70</Td>
                      <Td color="yellow.300">1:100</Td>
                    </Tr>
                  </Tbody>
                </Table>
              </Box>
            </Box>

            {/* Recent Claims */}
            <Box 
              bg="rgba(0, 30, 15, 0.8)" 
              borderRadius="lg" 
              p={4}
              border="1px solid rgba(0, 255, 128, 0.1)"
              position="relative"
              zIndex={1}
              overflow="hidden"
              height="100%"
            >
              <Heading size="sm" color="#4ADE80" mb={4} position="relative" zIndex={1}>Recent Claims</Heading>
              <Table size="sm" variant="unstyled" position="relative" zIndex={1}>
                <Thead>
                  <Tr>
                    <Th color="#4ADE80">PLAYER</Th>
                    <Th color="#4ADE80">PRIZE</Th>
                    <Th color="#4ADE80">TIME</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  <Tr>
                    <Td>
                      <Box 
                        bg="rgba(168, 85, 247, 0.2)" 
                        borderRadius="sm" 
                        py={0.5} 
                        px={2} 
                        display="inline-block"
                        border="1px solid rgba(168, 85, 247, 0.2)"
                      >
                        <Text color="purple.300" fontFamily="monospace" fontSize="xs">0x7f9...3F4E</Text>
                      </Box>
                    </Td>
                    <Td>
                      <Box 
                        bg="linear-gradient(90deg, rgba(132, 204, 22, 0.8) 0%, rgba(101, 163, 13, 0.8) 100%)" 
                        borderRadius="md" 
                        py={1} 
                        px={3} 
                        display="inline-block"
                        boxShadow="0 0 4px rgba(255, 255, 255, 0.15), 0 0 15px rgba(255, 255, 255, 0.1)"
                        border="0.75px solid rgba(255, 255, 255, 0.5)"
                        backdropFilter="blur(4px)"
                        minWidth="140px"
                        textAlign="center"
                        whiteSpace="nowrap"
                      >
                        <Text color="white" fontWeight="extrabold" fontSize="sm" letterSpacing="tight" textShadow="0 2px 5px rgba(0, 0, 0, 0.7)">400 JINU</Text>
                      </Box>
                    </Td>
                    <Td color="gray.400">2 minutes ago</Td>
                  </Tr>
                  <Tr>
                    <Td>
                      <Box 
                        bg="rgba(168, 85, 247, 0.2)" 
                        borderRadius="sm" 
                        py={0.5} 
                        px={2} 
                        display="inline-block"
                        border="1px solid rgba(168, 85, 247, 0.2)"
                      >
                        <Text color="purple.300" fontFamily="monospace" fontSize="xs">0x3B2...987C</Text>
                      </Box>
                    </Td>
                    <Td>
                      <Box 
                        bg="linear-gradient(90deg, rgba(16, 185, 129, 0.8) 0%, rgba(5, 150, 105, 0.8) 100%)" 
                        borderRadius="md" 
                        py={1} 
                        px={3} 
                        display="inline-block"
                        boxShadow="0 0 4px rgba(255, 255, 255, 0.15), 0 0 15px rgba(255, 255, 255, 0.1)"
                        border="0.75px solid rgba(255, 255, 255, 0.5)"
                        backdropFilter="blur(4px)"
                        minWidth="140px"
                        textAlign="center"
                        whiteSpace="nowrap"
                      >
                        <Text color="white" fontWeight="extrabold" fontSize="sm" letterSpacing="tight" textShadow="0 2px 5px rgba(0, 0, 0, 0.7)">1,000 JINU</Text>
                      </Box>
                    </Td>
                    <Td color="gray.400">5 minutes ago</Td>
                  </Tr>
                  <Tr>
                    <Td>
                      <Box 
                        bg="rgba(168, 85, 247, 0.2)" 
                        borderRadius="sm" 
                        py={0.5} 
                        px={2} 
                        display="inline-block"
                        border="1px solid rgba(168, 85, 247, 0.2)"
                      >
                        <Text color="purple.300" fontFamily="monospace" fontSize="xs">0xF12...6E8D</Text>
                      </Box>
                    </Td>
                    <Td>
                      <Box 
                        bg="linear-gradient(90deg, rgba(239, 68, 68, 0.8) 0%, rgba(220, 38, 38, 0.8) 100%)" 
                        borderRadius="md" 
                        py={1} 
                        px={3} 
                        display="inline-block"
                        boxShadow="0 0 4px rgba(255, 255, 255, 0.15), 0 0 15px rgba(255, 255, 255, 0.1)"
                        border="0.75px solid rgba(255, 255, 255, 0.5)"
                        backdropFilter="blur(4px)"
                        minWidth="140px"
                        textAlign="center"
                        whiteSpace="nowrap"
                      >
                        <Text color="white" fontWeight="extrabold" fontSize="sm" letterSpacing="tight" textShadow="0 2px 5px rgba(0, 0, 0, 0.7)">100 JINU</Text>
                      </Box>
                    </Td>
                    <Td color="gray.400">12 minutes ago</Td>
                  </Tr>
                  <Tr>
                    <Td>
                      <Box 
                        bg="rgba(168, 85, 247, 0.2)" 
                        borderRadius="sm" 
                        py={0.5} 
                        px={2} 
                        display="inline-block"
                        border="1px solid rgba(168, 85, 247, 0.2)"
                      >
                        <Text color="purple.300" fontFamily="monospace" fontSize="xs">0x8C4...2B10</Text>
                      </Box>
                    </Td>
                    <Td>
                      <Box 
                        bg="linear-gradient(90deg, rgba(234, 179, 8, 0.8) 0%, rgba(202, 138, 4, 0.8) 100%)" 
                        borderRadius="md" 
                        py={1} 
                        px={3} 
                        display="inline-block"
                        boxShadow="0 0 4px rgba(255, 255, 255, 0.15), 0 0 15px rgba(255, 255, 255, 0.1)"
                        border="0.75px solid rgba(255, 255, 255, 0.5)"
                        backdropFilter="blur(4px)"
                        minWidth="140px"
                        textAlign="center"
                        whiteSpace="nowrap"
                      >
                        <Text color="white" fontWeight="extrabold" fontSize="sm" letterSpacing="tight" textShadow="0 2px 5px rgba(0, 0, 0, 0.7)">300 JINU</Text>
                      </Box>
                    </Td>
                    <Td color="gray.400">18 minutes ago</Td>
                  </Tr>
                  <Tr>
                    <Td>
                      <Box 
                        bg="rgba(168, 85, 247, 0.2)" 
                        borderRadius="sm" 
                        py={0.5} 
                        px={2} 
                        display="inline-block"
                        border="1px solid rgba(168, 85, 247, 0.2)"
                      >
                        <Text color="purple.300" fontFamily="monospace" fontSize="xs">0x5E7...9D3F</Text>
                      </Box>
                    </Td>
                    <Td>
                      <Box 
                        bg="linear-gradient(90deg, rgba(59, 130, 246, 0.8) 0%, rgba(37, 99, 235, 0.8) 100%)" 
                        borderRadius="md" 
                        py={1} 
                        px={3} 
                        display="inline-block"
                        boxShadow="0 0 4px rgba(255, 255, 255, 0.15), 0 0 15px rgba(255, 255, 255, 0.1)"
                        border="0.75px solid rgba(255, 255, 255, 0.5)"
                        backdropFilter="blur(4px)"
                        minWidth="140px"
                        textAlign="center"
                        whiteSpace="nowrap"
                      >
                        <Text color="white" fontWeight="extrabold" fontSize="sm" letterSpacing="tight" textShadow="0 2px 5px rgba(0, 0, 0, 0.7)">10,000 JINU</Text>
                      </Box>
                    </Td>
                    <Td color="gray.400">25 minutes ago</Td>
                  </Tr>
                </Tbody>
              </Table>
            </Box>
          </SimpleGrid>
          
          {/* Prize Summary */}
          <Box 
            bg="rgba(0, 30, 15, 0.8)" 
            borderRadius="lg" 
            p={4}
            mb={6}
            border="1px solid rgba(0, 255, 128, 0.1)"
            position="relative"
            zIndex={1}
            overflow="hidden"
          >
            <Heading size="sm" color="#4ADE80" mb={4} position="relative" zIndex={1}>Prize Summary</Heading>
            <SimpleGrid columns={{ base: 1, sm: 2, md: 4 }} spacing={4} position="relative" zIndex={1}>
              <Box 
                bg="rgba(0, 20, 10, 0.6)" 
                p={4} 
                borderRadius="lg" 
                textAlign="center"
                border="1px solid rgba(255, 214, 0, 0.2)"
              >
                <Text color="yellow.300" fontSize="sm" fontWeight="bold" mb={2}>Top Prizes</Text>
                <Text color="white" fontSize="xl" fontWeight="bold">12 cards</Text>
                <Text color="yellow.300" fontSize="sm">350,000 JINU</Text>
              </Box>
              
              <Box 
                bg="rgba(0, 20, 10, 0.6)" 
                p={4} 
                borderRadius="lg" 
                textAlign="center"
                border="1px solid rgba(14, 165, 233, 0.2)"
              >
                <Text color="cyan.400" fontSize="sm" fontWeight="bold" mb={2}>Mid-tier Prizes</Text>
                <Text color="white" fontSize="xl" fontWeight="bold">420 cards</Text>
                <Text color="cyan.300" fontSize="sm">265,000 JINU</Text>
              </Box>
              
              <Box 
                bg="rgba(0, 20, 10, 0.6)" 
                p={4} 
                borderRadius="lg" 
                textAlign="center"
                border="1px solid rgba(74, 222, 128, 0.2)"
              >
                <Text color="#4ADE80" fontSize="sm" fontWeight="bold" mb={2}>Lower Prizes</Text>
                <Text color="white" fontSize="xl" fontWeight="bold">4200 cards</Text>
                <Text color="#4ADE80" fontSize="sm">720,000 JINU</Text>
              </Box>
              
              <Box 
                bg="rgba(0, 20, 10, 0.6)" 
                p={4} 
                borderRadius="lg" 
                textAlign="center"
                border="1px solid rgba(59, 130, 246, 0.2)"
              >
                <Text color="blue.400" fontSize="sm" fontWeight="bold" mb={2}>Win Odds</Text>
                <Text color="white" fontSize="xl" fontWeight="bold">1 in 2.15</Text>
                <Text color="blue.300" fontSize="sm">Top Prize: 1 in 10,000</Text>
              </Box>
            </SimpleGrid>
          </Box>
          
          {/* Randomness Verification */}
          <Box 
            bg="rgba(0, 30, 15, 0.8)" 
            borderRadius="lg" 
            p={4}
            position="relative"
            border="1px solid rgba(0, 255, 128, 0.1)"
            overflow="hidden"
          >
            <Icon 
              as={FaLock} 
              position="absolute" 
              top={4} 
              left={4} 
              color="#4ADE80" 
              boxSize={5} 
              zIndex={1}
            />
            <Heading size="sm" color="#4ADE80" mb={4} ml={8} position="relative" zIndex={1}>Randomness Verification</Heading>
            
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6} position="relative" zIndex={1}>
              <Box>
                <Text color="white" fontSize="sm" mb={2} fontWeight="bold">Provably Fair System</Text>
                <Text color="gray.300" fontSize="xs">
                  All game outcomes are determined using verifiable random
                  functions on the blockchain. This ensures complete transparency
                  and fairness for all players.
                </Text>
                <Button 
                  size="xs" 
                  colorScheme="blue" 
                  variant="outline" 
                  leftIcon={<FaInfoCircle />} 
                  mt={3}
                >
                  Verification Available On-Chain
                </Button>
              </Box>
              
              <Box>
                <Text color="white" fontSize="sm" mb={2} fontWeight="bold">Latest Block Hash</Text>
                <Text color="blue.200" fontSize="xs" fontFamily="monospace" bg="rgba(0, 20, 40, 0.5)" p={2} borderRadius="md" border="1px solid rgba(59, 130, 246, 0.2)">
                  0x7a31fe5d8c6e9b3a2f1d4e8c7b6a5d9eff7c0b5a4d3e2f1c
                </Text>
                <Text color="gray.400" fontSize="xs" mt={3}>
                  Block #48,456,932
                  <Text as="span" color="gray.500" ml={4}>Updated: 2 mins ago</Text>
                </Text>
              </Box>
            </SimpleGrid>
          </Box>
        </Box>
      </Box>
      
      {/* Win Modal */}
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader textAlign="center">🎉 Congratulations! 🎉</ModalHeader>
          <ModalBody>
            <VStack spacing={4}>
              {gameResult?.matchedValue && (
                <Text fontSize="lg" fontWeight="bold" textAlign="center" color="yellow.500">
                  You matched 3x {gameResult.matchedValue}!
                </Text>
              )}
              <Text fontSize="xl" fontWeight="bold" textAlign="center">
                You won {gameResult?.amount ? gameResult.amount.toLocaleString() : 0} JINU!
              </Text>
              <Text textAlign="center">
                Your winnings have been added to your balance.
              </Text>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              Close
            </Button>
            <Button colorScheme="green" onClick={handlePlayAgain}>
              Play Again
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default CashKingGame;
